/**
 * Frontend Analytics Event Registry
 * Import events from @artha/types for type safety
 */

import { FrontendEvent, type EventProperties } from "@artha/types";

/**
 * Event tracking helper with type safety
 */
export const ANALYTICS_EVENTS = {
  // Auth
  LOGIN_ATTEMPTED: FrontendEvent.LOGIN_ATTEMPTED,
  LOGIN_SUCCESS: FrontendEvent.LOGIN_SUCCESS,
  LOGIN_FAILED: FrontendEvent.LOGIN_FAILED,
  LOGOUT: FrontendEvent.LOGOUT,
  SIGNUP_STARTED: FrontendEvent.SIGNUP_STARTED,
  SIGNUP_COMPLETED: FrontendEvent.SIGNUP_COMPLETED,

  // Onboarding
  CR_SIGNUP: FrontendEvent.CR_SIGNUP,
  CR_INFO_GATHERING: FrontendEvent.CR_INFO_GATHERING,
  CR_SOCIALMEDIA_MANUAL: FrontendEvent.CR_SOCIALMEDIA_MANUAL,
  CR_NICHE_MANUAL: FrontendEvent.CR_NICHE_MANUAL,
  CR_PROFILE_BUILDER: FrontendEvent.CR_PROFILE_BUILDER,
  CR_PROFILE_APPEAL: FrontendEvent.CR_PROFILE_APPEAL,
  CR_ONBOARDING_WHATSAPP: FrontendEvent.CR_ONBOARDING_WHATSAPP,
  CR_ONBOARDING_SHAREJOB: FrontendEvent.CR_ONBOARDING_SHAREJOB,
  CR_ONBOARDING_POSTSTUDIO: FrontendEvent.CR_ONBOARDING_POSTSTUDIO,
  CR_ONBOARDING_BIOLINK: FrontendEvent.CR_ONBOARDING_BIOLINK,
  CR_ONBOARDING_COMPLETE: FrontendEvent.CR_ONBOARDING_COMPLETE,

  // Creator Dashboard & Navigation
  CR_DASHBOARD: FrontendEvent.CR_DASHBOARD,
  CR_LOGOUT: FrontendEvent.CR_LOGOUT,

  // Creator Profile Sharing
  CR_FIRST_PROFILE_SHARE: FrontendEvent.CR_FIRST_PROFILE_SHARE,
  CR_PROFILE_SHARE: FrontendEvent.CR_PROFILE_SHARE,
  CR_SHARE_PROFILE_COPYLINK: FrontendEvent.CR_SHARE_PROFILE_COPYLINK,
  CR_SHARE_PROFILE_REDIRECTION: FrontendEvent.CR_SHARE_PROFILE_REDIRECTION,
  CR_SEO_EDIT: FrontendEvent.CR_SEO_EDIT,
  CR_SHARE_PROFILE_EDIT: FrontendEvent.CR_SHARE_PROFILE_EDIT,

  // Creator Job Sharing
  CR_SHARE_FIRST_JOB: FrontendEvent.CR_SHARE_FIRST_JOB,
  CR_SHARE_JOB: FrontendEvent.CR_SHARE_JOB,
  CR_FIRST_JOB_APPLICATION: FrontendEvent.CR_FIRST_JOB_APPLICATION,

  // Creator Account Management
  CR_DELETE_ACCOUNT: FrontendEvent.CR_DELETE_ACCOUNT,
  CR_LINK_ACCOUNT: FrontendEvent.CR_LINK_ACCOUNT,

  // My Link - Profile Editing
  CR_EDIT_PROFILE: FrontendEvent.CR_EDIT_PROFILE,
  CR_EDIT_PROFILE_PIC: FrontendEvent.CR_EDIT_PROFILE_PIC,
  CR_EDIT_PROFILE_NAME: FrontendEvent.CR_EDIT_PROFILE_NAME,
  CR_EDIT_PROFILE_LINK: FrontendEvent.CR_EDIT_PROFILE_LINK,
  CR_EDIT_PROFILE_BIO: FrontendEvent.CR_EDIT_PROFILE_BIO,
  CR_EDIT_PROFILE_NICHE: FrontendEvent.CR_EDIT_PROFILE_NICHE,
  CR_EDIT_PROFILE_SOCIALLINK: FrontendEvent.CR_EDIT_PROFILE_SOCIALLINK,

  // My Link - Design
  CR_DESIGN_START: FrontendEvent.CR_DESIGN_START,
  CR_DESIGN_EDIT: FrontendEvent.CR_DESIGN_EDIT,

  // My Link - Settings
  CR_SETTINGS: FrontendEvent.CR_SETTINGS,
  CR_SETTINGS_NICHE_ADD: FrontendEvent.CR_SETTINGS_NICHE_ADD,
  CR_SETTINGS_NICHE_REMOVE: FrontendEvent.CR_SETTINGS_NICHE_REMOVE,
  CR_SETTINGS_JOBKW_ADD: FrontendEvent.CR_SETTINGS_JOBKW_ADD,
  CR_SETTINGS_JOBKW_REMOVE: FrontendEvent.CR_SETTINGS_JOBKW_REMOVE,
  CR_SETTINGS_COUNTRY_ADD: FrontendEvent.CR_SETTINGS_COUNTRY_ADD,
  CR_SETTINGS_COUNTRY_REMOVE: FrontendEvent.CR_SETTINGS_COUNTRY_REMOVE,
  CR_SETTINGS_EMPIND_ADD: FrontendEvent.CR_SETTINGS_EMPIND_ADD,
  CR_SETTINGS_EMPIND_REMOVE: FrontendEvent.CR_SETTINGS_EMPIND_REMOVE,
  CR_SETTINGS_EMPKW_ADD: FrontendEvent.CR_SETTINGS_EMPKW_ADD,
  CR_SETTINGS_EMPKW_REMOVE: FrontendEvent.CR_SETTINGS_EMPKW_REMOVE,

  // My Link - Filters
  CR_FILTER: FrontendEvent.CR_FILTER,
  CR_FILTER_JOBS: FrontendEvent.CR_FILTER_JOBS,
  CR_FILTER_COMPANY: FrontendEvent.CR_FILTER_COMPANY,

  // Earnings & Payout
  CR_EARNING: FrontendEvent.CR_EARNING,
  CR_PAYOUT_DOWNLOAD: FrontendEvent.CR_PAYOUT_DOWNLOAD,
  CR_PAYOUT_ADDED: FrontendEvent.CR_PAYOUT_ADDED,
  CR_FIRST_REVENUE: FrontendEvent.CR_FIRST_REVENUE,
  CR_REVENUE_UPDATE: FrontendEvent.CR_REVENUE_UPDATE,

  // Profile
  PROFILE_EDIT_STARTED: FrontendEvent.PROFILE_EDIT_STARTED,
  PROFILE_UPDATED: FrontendEvent.PROFILE_UPDATED,
  AVATAR_UPLOADED: FrontendEvent.AVATAR_UPLOADED,
  BIO_UPDATED: FrontendEvent.BIO_UPDATED,

  // Jobs
  JOB_CARD_VIEWED: FrontendEvent.JOB_CARD_VIEWED,
  JOB_CARD_CLICKED: FrontendEvent.JOB_CARD_CLICKED,
  JOB_SAVED: FrontendEvent.JOB_SAVED,
  JOB_UNSAVED: FrontendEvent.JOB_UNSAVED,
  JOB_APPLIED: FrontendEvent.JOB_APPLIED,
  JOB_SHARED: FrontendEvent.JOB_SHARED,

  // Search & Filters
  SEARCH_PERFORMED: FrontendEvent.SEARCH_PERFORMED,
  FILTER_APPLIED: FrontendEvent.FILTER_APPLIED,
  FILTER_CLEARED: FrontendEvent.FILTER_CLEARED,
  AUTOCOMPLETE_USED: FrontendEvent.AUTOCOMPLETE_USED,

  // Settings
  SETTINGS_OPENED: FrontendEvent.SETTINGS_OPENED,
  PREFERENCES_UPDATED: FrontendEvent.PREFERENCES_UPDATED,
  NOTIFICATION_SETTINGS_CHANGED: FrontendEvent.NOTIFICATION_SETTINGS_CHANGED,
  THEME_CHANGED: FrontendEvent.THEME_CHANGED,

  // Social
  SOCIAL_ACCOUNT_CONNECTED: FrontendEvent.SOCIAL_ACCOUNT_CONNECTED,
  SOCIAL_ACCOUNT_DISCONNECTED: FrontendEvent.SOCIAL_ACCOUNT_DISCONNECTED,
  LINKEDIN_SYNC_INITIATED: FrontendEvent.LINKEDIN_SYNC_INITIATED,

  // Navigation
  PAGE_VIEWED: FrontendEvent.PAGE_VIEWED,
  BOTTOM_SHEET_OPENED: FrontendEvent.BOTTOM_SHEET_OPENED,
  BOTTOM_SHEET_CLOSED: FrontendEvent.BOTTOM_SHEET_CLOSED,
  MODAL_OPENED: FrontendEvent.MODAL_OPENED,
  MODAL_CLOSED: FrontendEvent.MODAL_CLOSED,

  // Engagement
  BUTTON_CLICKED: FrontendEvent.BUTTON_CLICKED,
  LINK_CLICKED: FrontendEvent.LINK_CLICKED,
  FEATURE_DISCOVERED: FrontendEvent.FEATURE_DISCOVERED,

  // Audience
  AUD_SOCIALMEDIA_CLICK: FrontendEvent.AUD_SOCIALMEDIA_CLICK,
  AUD_JOB_VIEW: FrontendEvent.AUD_JOB_VIEW,
  AUD_JOB_APPLY: FrontendEvent.AUD_JOB_APPLY,
  AUD_JOB_SEARCH: FrontendEvent.AUD_JOB_SEARCH,
  AUD_JOB_FILTER_JOB: FrontendEvent.AUD_JOB_FILTER_JOB,
  AUD_EARN_CLICK: FrontendEvent.AUD_EARN_CLICK,
  AUD_VPN_RESTRICTION_SHOWN: FrontendEvent.AUD_VPN_RESTRICTION_SHOWN,
  AUD_COUNTRY_RESTRICTION_SHOWN: FrontendEvent.AUD_COUNTRY_RESTRICTION_SHOWN,
  AUD_INCOGNITO_RESTRICTION_SHOWN: FrontendEvent.AUD_INCOGNITO_RESTRICTION_SHOWN,
  AUD_JOB_ALERT_EMAIL_SUBMITTED: FrontendEvent.AUD_JOB_ALERT_EMAIL_SUBMITTED,
  AUD_JOB_ALERT_PREFS_SUBMITTED: FrontendEvent.AUD_JOB_ALERT_PREFS_SUBMITTED,
  AUD_JOB_ALERT_UNSUBSCRIBED: FrontendEvent.AUD_JOB_ALERT_UNSUBSCRIBED,
  AUD_RESUME_UPLOAD: FrontendEvent.AUD_RESUME_UPLOAD,
  AUD_RESUME_VERIFICATION: FrontendEvent.AUD_RESUME_VERIFICATION,
  AUD_RESUME_LOGIN: FrontendEvent.AUD_RESUME_LOGIN,
  AUD_RESUME_LOGOUT: FrontendEvent.AUD_RESUME_LOGOUT,

  // Post Studio
  CR_POSTSTUDIO: FrontendEvent.CR_POSTSTUDIO,
  CR_POSTSTUDIO_JOBSSELECT: FrontendEvent.CR_POSTSTUDIO_JOBSSELECT,
  CR_POSTSTUDIO_PLATFORM: FrontendEvent.CR_POSTSTUDIO_PLATFORM,
  CR_POSTSTUDIO_REGENERATE: FrontendEvent.CR_POSTSTUDIO_REGENERATE,
  CR_POSTSTUDIO_SHARE: FrontendEvent.CR_POSTSTUDIO_SHARE,
  CR_REFER_AND_EARN: FrontendEvent.CR_REFER_AND_EARN,
  CR_REFER_AND_EARN_COPYLINK: FrontendEvent.CR_REFER_AND_EARN_COPYLINK,
  CR_REFER_AND_EARN_SHARE: FrontendEvent.CR_REFER_AND_EARN_SHARE,
  CR_REFER_AND_EARN_WHATSAPPINVITE: FrontendEvent.CR_REFER_AND_EARN_WHATSAPPINVITE,
  CR_REFER_AND_EARN_RESOURCES_PLAYBOOK: FrontendEvent.CR_REFER_AND_EARN_RESOURCES_PLAYBOOK,
  CR_REFER_AND_EARN_RESOURCES_IDEA: FrontendEvent.CR_REFER_AND_EARN_RESOURCES_IDEA,
  CR_REFER_AND_EARN_RESOURCES_VIDEO: FrontendEvent.CR_REFER_AND_EARN_RESOURCES_VIDEO,
  REFER_AND_EARN_INVITATION: FrontendEvent.REFER_AND_EARN_INVITATION,
  CR_EARN_MODAL_CLICK: FrontendEvent.CR_EARN_MODAL_CLICK,
  CR_EARN_LEADERBOARD_CLICK: FrontendEvent.CR_EARN_LEADERBOARD_CLICK,
} as const;

/**
 * Type-safe event tracking function signature
 */
export type TrackEventFn = (
  event: FrontendEvent,
  properties?: EventProperties
) => void;
